import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Button from "../../../../components/button/Button";
import AuthTextInput from "../../../../components/inputs/authTextInput/AuthTextInput";
import DateInput from "../../../../components/inputs/dateInput/DateInput";
import ImagesDropdown from "../../../../components/inputs/imagesDropdown/ImagesDropdown";
import NumberInput from "../../../../components/inputs/numberInput/NumberInput";
import SelectInput from "../../../../components/inputs/selectInput/SelectInput";
import Loader from "../../../../components/loader/Loader";
import SettingsPagesContainer from "../../../../components/settingsPagesContainer/SettingsPagesContainer";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import { showConfirmationModal } from "../../../../features/confirmationModal/ConfirmationModalSlice";
import { fetchTournamentImages } from "../../../../features/images/tournamentImages/tournamentImagesSlice";
import { fetchTournamentsStatuses } from "../../../../features/tournaments/tournamentsSlice";
import { fetchTournamentDetailsTemplateOptions } from "../../../../features/tournamentDetailsTemplates/tournamentDetailsTemplatesSlice";
import { getUTCDateWithPeakTimeFromDate } from "../../../../utils/timeZone";
import styles from "./editTournament.module.css";

const EditTournament = () => {
  const location = useLocation();

  const {
    tournamentId: fetchedTournamentId,
    status: fetchedTournamentStatus,
    name: fetchedTournamentName,
    startDate: fetchedTournamentStartDate,
    prize: fetchedTournamentPrize,
    tournamentImage: fetchedTournamentImage,
    detailsTemplate: fetchedDetailsTemplate,
    liveLink: fetchedTournamentLiveLink,
    stage: fetchedTournamentStage,
  } = location.state;

  const date = new Date(fetchedTournamentStartDate);
  const formatedStartDate = date.toISOString().split("T")[0];

  const [tournamentStatus, setTournamentStatus] = useState(
    fetchedTournamentStatus
  );
  const [liveLink, setLiveLink] = useState(fetchedTournamentLiveLink);
  // Admin-set stage marker (qualifiers | semifinals | finals). Only meaningful
  // while the tournament is live with no stream link — that's the only case the
  // app's displayStatus reads `stage` for the badge/timer.
  const [stage, setStage] = useState(fetchedTournamentStage || "qualifiers");

  const [tournamentName, setTournamentName] = useState(fetchedTournamentName);

  const [startDate, setStartDate] = useState(formatedStartDate);
  const [prize, setPrize] = useState(fetchedTournamentPrize);
  const [image, setImage] = useState(fetchedTournamentImage);
  // detailsTemplate may arrive as an id string or a populated object.
  const [detailsTemplate, setDetailsTemplate] = useState(
    fetchedDetailsTemplate?._id || fetchedDetailsTemplate || ""
  );

  const dispatch = useDispatch();

  const { fetchStatusesStatus, statusesList } = useSelector(
    (state) => state.tournaments
  );

  const { fetchStatus: imagesFetchStatus, list: tournamentImages } =
    useSelector((state) => state.tournamentImages);

  const { options: templateOptions, optionsFetchStatus: templatesFetchStatus } =
    useSelector((state) => state.tournamentDetailsTemplates);

  const handleTournamentUpdate = () => {
    dispatch(
      showConfirmationModal({
        message: "هل انت متاكد من التعديل على البطولة ؟",
        data: {
          tournamentId: fetchedTournamentId,
          tournamentData: {
            name: tournamentName,
            status: tournamentStatus,
            startDate: getUTCDateWithPeakTimeFromDate(startDate),
            prize: prize,
            tournamentImage: image,
            detailsTemplate: detailsTemplate || null,
            liveLink: liveLink,
            stage: stage,
          },
        },
        actionType: "updateTournament",
      })
    );
  };

  useEffect(() => {
    if (fetchStatusesStatus === "idle") {
      dispatch(fetchTournamentsStatuses());
    }

    if (imagesFetchStatus === "idle") {
      dispatch(fetchTournamentImages());
    }

    if (templatesFetchStatus === "idle") {
      dispatch(fetchTournamentDetailsTemplateOptions());
    }
  }, [dispatch, fetchStatusesStatus, imagesFetchStatus, templatesFetchStatus]);

  if (
    fetchStatusesStatus === "loading" ||
    imagesFetchStatus === "loading" ||
    templatesFetchStatus === "loading"
  ) {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <SettingsPagesContainer
        title={`التعديل على البطولة : ${fetchedTournamentName}`}
        fullHeight
        displayContainerClassName={styles.displayContainer}
      >
        <div className={styles.formLayout}>
          <div className={styles.fieldsWrapper}>
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <TextWrapper textStyle="textMid">المعلومات الأساسية</TextWrapper>
              </div>
              <div className={styles.sectionGrid}>
                <div className={`${styles.inputWrapper} ${styles.fullSpan}`}>
                  <TextWrapper textStyle="textMid">اسم البطولة</TextWrapper>
                  <AuthTextInput
                    height="52px"
                    width="100%"
                    borderRadius="12px"
                    placeholder="اسم البطولة"
                    bgColor={"grey"}
                    value={tournamentName}
                    setValue={setTournamentName}
                  />
                </div>
                <div className={styles.inputWrapper}>
                  <TextWrapper textStyle="textMid">حالة البطولة</TextWrapper>
                  <SelectInput
                    placeHolder={"حالة البطولة"}
                    data={statusesList}
                    value={tournamentStatus}
                    setValue={setTournamentStatus}
                    wrapperClassName={styles.fullWidthField}
                    className={styles.fullWidthSelectButton}
                  >
                    {Array.isArray(statusesList) &&
                      statusesList.map((item) => {
                        return (
                          <option value={item} key={item}>
                            {item === "upcoming"
                              ? "قادمة"
                              : item === "live"
                                ? "مباشرة"
                                : "منتهية"}
                          </option>
                        );
                      })}
                  </SelectInput>
                </div>
                {tournamentStatus === "live" && (
                  <div className={`${styles.inputWrapper} ${styles.fullSpan}`}>
                    <TextWrapper textStyle="textMid">
                      رابط البث المباشر
                    </TextWrapper>
                    <AuthTextInput
                      height="52px"
                      width="100%"
                      borderRadius="12px"
                      placeholder="رابط البث المباشر"
                      bgColor={"grey"}
                      value={liveLink}
                      setValue={setLiveLink}
                    />
                  </div>
                )}
                {/* Stage only drives the app badge while live with no stream
                    link, so it's surfaced alongside the live fields. */}
                {tournamentStatus === "live" && (
                  <div className={styles.inputWrapper}>
                    <TextWrapper textStyle="textMid">المرحلة</TextWrapper>
                    <SelectInput
                      placeHolder={"المرحلة"}
                      value={stage}
                      setValue={setStage}
                      wrapperClassName={styles.fullWidthField}
                      className={styles.fullWidthSelectButton}
                    >
                      <option value="qualifiers">تصفيات</option>
                      <option value="semifinals">نصف النهائيات</option>
                      <option value="finals">نهائيات</option>
                    </SelectInput>
                  </div>
                )}
                <div className={styles.inputWrapper}>
                  <TextWrapper textStyle="textMid">تاريخ البداية</TextWrapper>
                  <DateInput
                    value={startDate}
                    setValue={setStartDate}
                    className={styles.fullWidthControl}
                  />
                </div>
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <TextWrapper textStyle="textMid">الجائزة والقالب</TextWrapper>
              </div>
              <div className={styles.sectionGrid}>
                <div className={styles.inputWrapper}>
                  <TextWrapper textStyle="textMid">جائزة البطولة</TextWrapper>
                  <NumberInput
                    placeHolder={"جائزة البطولة"}
                    value={prize}
                    setValue={setPrize}
                    className={styles.fullWidthControl}
                  />
                </div>
                <div className={styles.inputWrapper}>
                  <TextWrapper textStyle="textMid">
                    قالب تفاصيل البطولة
                  </TextWrapper>
                  <SelectInput
                    placeHolder={"اختر قالب التفاصيل (اختياري)"}
                    value={detailsTemplate}
                    setValue={setDetailsTemplate}
                    wrapperClassName={styles.fullWidthField}
                    className={styles.fullWidthSelectButton}
                  >
                    {Array.isArray(templateOptions) &&
                      templateOptions.map((template) => (
                        <option value={template._id} key={template._id}>
                          {template.name}
                        </option>
                      ))}
                  </SelectInput>
                </div>
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <TextWrapper textStyle="textMid">صورة البطولة</TextWrapper>
              </div>
              <ImagesDropdown
                data={tournamentImages}
                value={image}
                setValue={setImage}
                className={styles.imagesGrid}
                itemClassName={styles.imageOption}
              />
            </section>
          </div>
          <div className={styles.createNewTournamentBtnWrapper}>
            <Button
              size="bigSize"
              variant="surfaceVariant"
              functionallity={handleTournamentUpdate}
            >
              <TextWrapper textStyle="textMid">التعديل على البطولة</TextWrapper>
            </Button>
          </div>
        </div>
      </SettingsPagesContainer>
    </div>
  );
};

export default EditTournament;
